import type { CommonRequestOptions, FetchParam } from "app/services/fetchService";
import { checkResultWithFullResponse, DefaultFetchService } from "app/services/fetchService";

export interface IWidgetResource {
    websiteAppsResource: (
        metric: string,
        config?: CommonRequestOptions,
    ) => Record<string, (params: Record<string, any>) => Promise<any>>;
    resourceByController: (
        componentId: string,
        metric: string,
        config?: CommonRequestOptions,
    ) => Record<string, (params: Record<string, any>) => Promise<any>>;
    resourceByEndpoint: (
        endpoint: string,
        config?: CommonRequestOptions,
    ) => Record<string, (params: Record<string, any>) => Promise<any>>;
}

type WidgetFetcher = (params: FetchParam) => Promise<unknown>;

type WidgetTableResult = {
    Data?: unknown;
    Records?: unknown;
};

const createEmptyWidgetResult = (): WidgetTableResult => ({
    Data: [],
    Records: [],
});

const attachRecords = (response: unknown): unknown => {
    if (response == null) {
        return createEmptyWidgetResult();
    }

    if (checkResultWithFullResponse(response)) {
        const fullResponse = response as { result: WidgetTableResult | null };
        if (!fullResponse.result) {
            fullResponse.result = createEmptyWidgetResult();
            return fullResponse;
        }
        fullResponse.result.Records = fullResponse.result.Data;
        return fullResponse;
    }

    const result = response as WidgetTableResult;
    result.Records = result.Data;
    return response;
};

const applyDigest = (promise: WidgetFetcher) => {
    return (params: FetchParam) =>
        promise(params).then((res) => {
            return res;
        });
};

// Resource for website/apps widgets
function WidgetResourceFactory(): IWidgetResource {
    const fetchService = DefaultFetchService.getInstance();

    const websiteAppsResource = function (metric: string, config?: CommonRequestOptions) {
        const endPointPrefix = "/api/WidgetKpis";
        // const timeout = 300000; // TODO: add timeout!
        return {
            Settings: applyDigest((params) => {
                return fetchService
                    .send<any>(`${endPointPrefix}/${metric}/GetMetricMetaData`, {
                        method: "GET",
                        params,
                        ...config,
                    })
                    .then(attachRecords);
            }),
            Table: applyDigest((params) => {
                return fetchService
                    .send<any>(`${endPointPrefix}/${metric}/GetTableData`, {
                        method: "GET",
                        params,
                        ...config,
                    })
                    .then(attachRecords);
            }),
            SingleMetric: applyDigest((params) => {
                return fetchService.send<any>(`${endPointPrefix}/${metric}/GetSingleMetricData`, {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            Graph: applyDigest((params) => {
                return fetchService.send<any>(`${endPointPrefix}/${metric}/GetGraphData`, {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            PieChart: applyDigest((params) => {
                return fetchService.send<any>(`${endPointPrefix}/${metric}/GetPieChartData`, {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            BarChart: applyDigest((params) => {
                return fetchService.send<any>(`${endPointPrefix}/${metric}/GetPieChartData`, {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
        };
    };

    // Resource for widgets based on componentId
    const resourceByController = function (componentId: string, metric: string, config?: CommonRequestOptions) {
        const endPointPrefix = "/widgetApi/" + componentId;
        // const timeout = 300000; // TODO: add timeout!
        return {
            Settings: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/MetricMetaData", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            Table: applyDigest((params) => {
                return fetchService
                    .send<any>(endPointPrefix + "/" + metric + "/Table", {
                        method: "GET",
                        params,
                        ...config,
                    })
                    .then(attachRecords);
            }),
            Data: applyDigest((params) => {
                return fetchService
                    .send<any>(endPointPrefix + "/" + metric + "/Data", {
                        method: "GET",
                        params,
                        ...config,
                    })
                    .then(attachRecords);
            }),
            SingleMetric: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/SingleMetric", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            Graph: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/Graph", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            SwitchGraph: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/SwitchGraph", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            PieChart: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/PieChart", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            BarChart: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/PieChart", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
            GraphPOP: applyDigest((params) => {
                return fetchService.send<any>(endPointPrefix + "/" + metric + "/GraphPOP", {
                    method: "GET",
                    params,
                    ...config,
                });
            }),
        };
    };
    // Resource for widgets based on provided endpoint
    const resourceByEndpoint = function (endpoint: string, config?: CommonRequestOptions) {
        return {
            Settings: (params: FetchParam) => {
                return fetchService.send<any>(endpoint + "/MetricMetaData", {
                    method: "GET",
                    params,
                    ...config,
                });
            },
            Table: (params: FetchParam) => {
                return fetchService.send<any>(endpoint + "/Table", {
                    method: "GET",
                    params,
                    ...config,
                });
            },
            SingleMetric: (params: FetchParam) => {
                return fetchService.send<any>(endpoint + "/SingleMetric", {
                    method: "GET",
                    params,
                    ...config,
                });
            },
            Graph: (params: FetchParam) => {
                return fetchService.send<any>(endpoint + "/Graph", {
                    method: "GET",
                    params,
                    ...config,
                });
            },
            Data: (params: FetchParam) => {
                return fetchService.send<any>(endpoint + "/Data", {
                    method: "GET",
                    params,
                    ...config,
                });
            },
            PieChart: (params: FetchParam) => {
                return fetchService.send(endpoint + "/PieChart", {
                    method: "GET",
                    params,
                    ...config,
                });
            },
        };
    };

    return {
        websiteAppsResource,
        resourceByController,
        resourceByEndpoint,
    };
}

export default WidgetResourceFactory();
